// Salesforce Types
export interface OpportunityBatch {
  name: string;
  description?: string;
  totalAmount: number;
  totalOpportunities: number;
  status: string;
  createdDate: string;
  postedToSageDateTime?: string | null;
  hasPostingSnapshot?: boolean;
}

export interface Opportunity {
  id: string;
  name: string;
  amount: number;
  closeDate: string;
  stageName: string;
  accountName: string;
  batchName?: string;
  allocations: Allocation[];
}

export interface Allocation {
  id: string;
  opportunityId: string;
  amount: number;
  donationChannel: string;
  paymentType: string;
  bankAccount: string;
  contributionCode?: string;
  pfpCode?: string;
  locationCode?: string;
  departmentCode?: string;
  projectCode?: string;
  classCode?: string;
}

// Journal Types Enum (matches original JournalTypes enum)
export enum JournalTypes {
  Bank = "Bank",
  Revenue = "Revenue",
  Tax = "Tax",
  Expense = "Expense",
}

// Dropdown Item for dimension codes
export interface DropdownItem {
  value: string;
  label: string;
}

// Journal Entry Types (updated to match original Blazor JournalEntry)
export interface JournalEntry {
  id: string;
  journalType: JournalTypes;
  memo: string;
  accountNumber: string;
  accountName: string;
  departmentId: string;
  projectId: string;
  classId: string;
  pfpCode: string;
  contributionCode: string;
  debit: number | null;
  credit: number | null;
  taxId: string;
  taxAmount: number | null;
  description: string;
  locationCode?: string;
  departmentCode?: string;
  projectCode?: string;
  classCode?: string;
  validations: ValidationEvent[];
  // Underlying Salesforce Opportunity IDs aggregated into this entry (see server model).
  // Used to pair Revenue rows with their offsetting Bank/Expense rows by shared source
  // opportunity instead of coincidental amount equality.
  opportunityIds?: string[];
}

export interface ValidationEvent {
  severity: "error" | "warning" | "info";
  message: string;
  field?: string;
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  count?: number;
  message?: string;
  pagination?: PaginationMeta;
  error?: {
    message: string;
    stack?: string;
  };
}

export interface PaginationMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/**
 * The result of validating a set of entries before posting. Named rather than
 * left inline on the service method, so the contract has somewhere to live.
 *
 * Matches what journal.controller actually responds with. The inline type this
 * replaced also declared `hasWarnings`, which the server has never sent — a
 * harmless fiction while it was buried in a call site, and a shared lie once
 * named. `totals` was a structural copy of JournalTotals.
 */
export interface JournalValidationResult {
  entries: JournalEntry[];
  totals: JournalTotals;
  hasErrors: boolean;
}

export interface JournalBuildResult {
  entries: JournalEntry[];
  totals: JournalTotals;
  opportunityCount: number;
  batchAmount: number;
}

export interface JournalTotals {
  totalDebit: number;
  totalCredit: number;
  bankTotal: number;
  revenueTotal: number;
  balance: number;
  isBalanced: boolean;
}

export interface JournalPostResult {
  success: boolean;
  journalId?: string;
  message: string;
  errors?: string[];
  skippedSagePosting?: boolean;
}

// Dimension Codes Response
export interface DimensionCodes {
  pfpCodes: DropdownItem[];
  contributionCodes: DropdownItem[];
  locationCodes: DropdownItem[];
  departmentCodes: DropdownItem[];
  projectCodes: DropdownItem[];
  classCodes: DropdownItem[];
  accountCodes: DropdownItem[];
}

// Journal Draft - saved in-progress edits so a user can resume a journal later
export interface JournalDraft {
  id: string;
  batchName: string;
  description: string;
  postDate: string;
  location: string;
  entries: JournalEntry[];
  savedAt: string;
  createdAt: string;
}

// Posting Snapshot - records the journal entries at time of posting
export interface PostingSnapshot {
  id: string;
  batchName: string;
  journalId: string;
  description: string;
  postDate: string;
  location: string;
  entries: JournalEntry[];
  totals: JournalTotals;
  opportunityCount: number;
  batchAmount: number;
  postedAt: string;
  createdAt: string;
}
